// pages/AlbAccessLogsView.jsx — analisa ALB access logs (S3) per load balancer.
// Backend: GET /api/ec2-health/alb-logs?account=&lb=&hours=  (backend/albAccessLogs.js), read-only.
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../context/AuthContext';
import { TrendChart } from './Ec2HealthHistory';

const RANGES = [[1, '1 h'], [6, '6 h'], [24, '24 h'], [48, '48 h']];
const n = (v, d = 0) => (v == null ? '–' : Number(v).toLocaleString('en-US', { maximumFractionDigits: d }));
const sec = (v) => (v == null ? '–' : v < 1 ? `${Math.round(v * 1000)} ms` : `${n(v, 1)} s`);
const wib = (t) => new Date(t).toLocaleString('id-ID', { timeZone: 'Asia/Jakarta', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
const codeCls = (c) => (/^5|^-|460|499/.test(c) ? 'bg-red-100 text-red-700' : /^4/.test(c) ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700');

function Card({ title, children }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">{title}</div>
      {children}
    </div>
  );
}

function RouteTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-left text-gray-500"><th className="py-1 pr-2">Route</th><th className="px-2 text-right">Req</th><th className="px-2 text-right">Err</th><th className="px-2 text-right">p95</th><th className="pl-2 text-right">Max</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.route} className="border-t border-gray-100">
              <td className="max-w-[420px] truncate py-1 pr-2 font-mono text-gray-700" title={r.route}>{r.route}</td>
              <td className="px-2 text-right tabular-nums">{n(r.req)}</td>
              <td className={`px-2 text-right tabular-nums ${r.err ? 'text-red-600' : ''}`}>{n(r.err)}</td>
              <td className="px-2 text-right tabular-nums">{sec(r.p95)}</td>
              <td className={`pl-2 text-right tabular-nums ${r.max > 30 ? 'text-red-600' : ''}`}>{sec(r.max)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AlbAccessLogsView({ account, lb }) {
  const [hours, setHours] = useState(6);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback((refresh) => {
    setLoading(true); setErr(null);
    api.get(`/api/ec2-health/alb-logs?account=${account}&lb=${lb}&hours=${hours}${refresh ? '&refresh=1' : ''}`)
      .then((r) => setData(r.data)).catch((e) => setErr((e.response && e.response.data && e.response.data.error) || e.message))
      .finally(() => setLoading(false));
  }, [account, lb, hours]);
  useEffect(() => { load(false); }, [load]);

  const d = data || {};
  const t = d.totals || {};
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center rounded-lg bg-slate-100/80 p-0.5 text-xs">
          {RANGES.map(([h, l]) => (
            <button key={h} type="button" onClick={() => setHours(h)}
              className={`rounded-md px-2.5 py-1 font-medium ${hours === h ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>{l}</button>
          ))}
        </div>
        <span className="text-xs text-gray-500">
          {data && <>s3://{d.bucket || '–'} · {d.enabled ? 'logging on' : 'logging OFF'} · {n(d.files)} files · {n(d.lines)} requests{d.newest ? ` · newest file ${wib(d.newest)}` : ''}</>}
        </span>
        <button type="button" onClick={() => load(true)} disabled={loading}
          className="ml-auto rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          {loading ? 'Loading…' : 'Refresh'}</button>
      </div>
      {err && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {data && !d.enabled && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">Access logs are disabled on this load balancer; only older files (if any) are shown.</div>}
      {data && d.enabled && !d.lines && <div className="rounded-lg border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400">No log files in this range yet. ALB delivers a file every 5 minutes per node.</div>}

      {data && d.lines > 0 && (
        <>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <Card title="Requests"><div className="text-2xl font-semibold tabular-nums">{n(t.req)}</div></Card>
            <Card title="Errors (5xx / 499 / no response)"><div className={`text-2xl font-semibold tabular-nums ${t.err ? 'text-red-600' : ''}`}>{n(t.err)}</div><div className="text-xs text-gray-400">{t.req ? n(t.err / t.req * 100, 2) : 0}%</div></Card>
            <Card title="Status codes">
              <div className="flex flex-wrap gap-1">{(d.status || []).map((s) => <span key={s.code} className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${codeCls(s.code)}`}>{s.code} · {n(s.n)}</span>)}</div>
            </Card>
            <Card title="Top clients">
              {(d.clients || []).slice(0, 4).map((c) => <div key={c.client} className="flex justify-between text-xs"><span className="font-mono">{c.client}</span><span className="tabular-nums">{n(c.req)}{c.err ? <span className="text-red-600"> / {n(c.err)} err</span> : ''}</span></div>)}
            </Card>
          </div>

          <Card title="Per target (server)">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-gray-500"><th className="py-1">Server</th><th className="text-right">Req</th><th className="text-right">Err</th><th className="text-right">p50</th><th className="text-right">p95</th><th className="text-right">Max</th></tr></thead>
              <tbody>{(d.targets || []).map((x) => (
                <tr key={x.target} className="border-t border-gray-100">
                  <td className="py-1 font-medium text-gray-700">{x.name || x.target} <span className="font-normal text-gray-400">{x.name ? x.target : ''}</span></td>
                  <td className="text-right tabular-nums">{n(x.req)}</td>
                  <td className={`text-right tabular-nums ${x.err ? 'text-red-600' : ''}`}>{n(x.err)}</td>
                  <td className="text-right tabular-nums">{sec(x.p50)}</td><td className="text-right tabular-nums">{sec(x.p95)}</td>
                  <td className={`text-right tabular-nums ${x.max > 30 ? 'text-red-600' : ''}`}>{sec(x.max)}</td>
                </tr>))}</tbody>
            </table>
          </Card>

          <Card title="Requests, errors and slowest response per 5 min">
            <TrendChart days={hours / 24} unit="" height={140} series={[
              { name: 'Requests', color: '#0ea5e9', points: (d.timeline || []).map((p) => [p[0], p[1]]) },
              { name: 'Errors', color: '#ef4444', points: (d.timeline || []).map((p) => [p[0], p[2]]) },
              { name: 'Max target time (s)', color: '#f59e0b', dashed: true, points: (d.timeline || []).map((p) => [p[0], p[3]]) },
            ]} />
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Slowest routes (target processing time)"><RouteTable rows={d.slowest || []} /></Card>
            <Card title="Busiest routes"><RouteTable rows={d.busiest || []} /></Card>
          </div>

          <Card title={`Latest errors (${(d.errors || []).length})`}>
            {!(d.errors || []).length ? <div className="text-xs text-emerald-700">No errors in this range.</div> : (
              <div className="max-h-80 overflow-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-gray-500"><th className="py-1 pr-2">Time (WIB)</th><th className="pr-2">Code</th><th className="pr-2">Server</th><th className="pr-2">Client</th><th className="pr-2 text-right">Target time</th><th>Request</th></tr></thead>
                  <tbody>{d.errors.map((e, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="whitespace-nowrap py-1 pr-2 tabular-nums">{wib(e.t)}</td>
                      <td className="pr-2"><span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${codeCls(e.elb)}`}>{e.elb}{e.tgt !== '-' && e.tgt !== e.elb ? ` / ${e.tgt}` : ''}</span></td>
                      <td className="whitespace-nowrap pr-2">{e.targetName || e.target}</td>
                      <td className="pr-2 font-mono">{e.client}</td>
                      <td className="pr-2 text-right tabular-nums">{e.tgtT < 0 ? 'no response' : sec(e.tgtT)}</td>
                      <td className="max-w-[420px] truncate font-mono" title={e.path}>{e.method} {e.path}</td>
                    </tr>))}</tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
      <div className="text-[11px] leading-relaxed text-gray-400">
        Read-only from the ALB access log bucket (S3). Up to 400 newest files per range; static assets are left out of the route tables. Cached for 2 minutes.
      </div>
    </div>
  );
}
